import React from 'react'
import Helmet from 'react-helmet'
import { helmetJsonLdProp } from 'react-schemaorg'
import useSiteMetadata from '@helpers-blog/useSiteMetadata'
import { descriptionForSlug } from '../../../seo-descriptions'
import { alternatesFor } from '../../../seo-hreflang'
import { graphql, useStaticQuery } from "gatsby"
// import defaultImg from "../images/default-og-image.jpg"
// import { globalHistory } from "@reach/router"

const DEFAULT_OG_THUMBNAIL = "default-og-image"
//TODO: check if obsolete due to manifest plugin
const FAVICON = "favicon"
// Square mark generated by gatsby-plugin-manifest; schema.org wants >= 112px.
const PUBLISHER_LOGO_PATH = "/icons/icon-512x512.png"

// Fallback language when the site neither passes `lang` nor declares one in
// siteMetadata. Branch builds (e.g. the English site) set siteMetadata.lang
// instead of touching this component.
const DEFAULT_LANG = "de"

const trimTrailingSlash = value => (value || "").replace(/\/+$/, "")

const absoluteUrl = (base, path) => {
    const root = trimTrailingSlash(base)
    if (!path) return root ? `${root}/` : ""
    if (/^https?:\/\//i.test(path)) return path
    const suffix = path.startsWith("/") ? path : `/${path}`
    return root ? `${root}${suffix}` : suffix
}

/**
 * Contentful hands this component a display date ("April 08, 2022").
 * schema.org requires ISO 8601, so normalise what we can parse and drop what
 * we cannot rather than shipping an invalid datePublished.
 */
const toIsoDate = value => {
    if (!value) return null
    if (value instanceof Date) {
        return Number.isNaN(value.getTime())
            ? null
            : value.toISOString().slice(0, 10)
    }
    if (typeof value !== "string") return null
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime())
        ? null
        : parsed.toISOString().slice(0, 10)
}

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

/**
 * The title prop sometimes already carries the brand suffix; keep the
 * machine-readable headline free of it.
 */
const stripBrandSuffix = (title, brand) => {
    if (!title || !brand) return title
    const cleaned = title
        .replace(new RegExp(`\\s*\\|\\s*${escapeRegExp(brand)}.*$`, "i"), "")
        .trim()
    return cleaned || title
}

const Seo = ({
                 title,
                 description,
                 excerpt,
                 meta,
                 keywords,
                 author,
                 category,
                 date,
                 dateISO,
                 dateModified,
                 timeToRead,
                 children,
                 thumbnail,
                 siteUrl,
                 slug,
                 canonicalPath,
                 lang,
                 noindex,
                 type,
                 body
                 }) => {

     const filesQuery = useStaticQuery(
        graphql`
          {
            allFile(limit: 2, filter: {name: {in: ["favicon", "default-og-image"]}}) {
              nodes {
                publicURL
              }
            }
          }
        `
    )
    const staticFileUrls = new Map()
    if(filesQuery && filesQuery.allFile && filesQuery.allFile.nodes){
        filesQuery.allFile.nodes.forEach(file => {
            const fileUrl = file.publicURL
            if (fileUrl.includes(FAVICON)) {
                staticFileUrls.set(FAVICON, fileUrl)
            } else if (fileUrl.includes(DEFAULT_OG_THUMBNAIL)) {
                staticFileUrls.set(DEFAULT_OG_THUMBNAIL, fileUrl)
            }
        })
    }

    const site = useSiteMetadata()

    // Single source of truth: siteMetadata.siteUrl. The pageContext fallback
    // keeps call sites that still pass siteUrl working.
    const baseUrl = trimTrailingSlash(site.siteUrl || siteUrl || "")
    const brandName = site.name || site.title
    const language = lang || site.lang || DEFAULT_LANG

    const social = (author && author.social) || site.social || []
    const twitter =
        social.find(s => s.name && s.name.toLowerCase() === 'twitter') || {}

    const pageTitle = title || site.title

    /**
     * The <title> tag, with the brand appended only when it fits.
     *
     * Appending " | Primula Medical Travel" (24 chars) to every title spends a
     * third of the ~65 characters Google renders before truncating, which on
     * this site pushed seven articles over the limit. Dropping the suffix when
     * it would overflow keeps the keywords that actually rank and still shows
     * the brand on the pages where there is room.
     */
    const TITLE_LIMIT = 65
    const withBrand = `${pageTitle} | ${brandName}`
    const helmetTitle =
        withBrand.length <= TITLE_LIMIT ? withBrand : pageTitle

    /**
     * Description fallback chain.
     *
     * Candidates are cleaned and validated, because a raw value can be
     * unusable: an article without an excerpt once shipped Babel runtime JS as
     * its meta description (see seo-descriptions.js for the full story — in
     * short, `body` is compiled MDX, not text).
     *
     * Order: CMS excerpt -> explicit description prop -> per-page override ->
     * site default. `body` is deliberately absent: it holds compiled MDX
     * component source at build time, so no amount of tag-stripping turns it
     * into prose.
     */
    const cleanText = raw =>
        String(raw || "")
            .replace(/<script[\s\S]*?<\/script>/gi, " ") // code blocks must go whole
            .replace(/<style[\s\S]*?<\/style>/gi, " ")
            .replace(/^\[[^\]]+\]:.*$/gm, " ") // markdown reference links
            .replace(/<[^>]+>/g, " ") // raw HTML bodies
            .replace(/[#*_>`]/g, " ")
            .replace(/&[a-z]+;/gi, " ")
            .replace(/\s+/g, " ")
            .trim()

    const looksLikeCode = text =>
        /\b(function|var|const|let|return|extends)\b\s*[({=]/.test(text) ||
        /=>/.test(text) ||
        /Object\.assign|prototype|console\.|window\.|mdx\(/.test(text)

    const usable = raw => {
        const text = cleanText(raw)
        if (!text) return ""
        if (looksLikeCode(text)) return ""
        // reject fragments ("var x", stray labels); keep legitimate short text
        if (text.replace(/[^A-Za-zÄÖÜäöüß]/g, "").length < 20) return ""
        if (text.length <= 155) return text
        const clipped = text.slice(0, 155)
        const lastSpace = clipped.lastIndexOf(" ")
        return `${clipped.slice(0, lastSpace > 80 ? lastSpace : 155)}…`
    }

    const pageDescription =
        usable(excerpt) ||
        usable(description) ||
        usable(descriptionForSlug(slug || canonicalPath)) ||
        usable(site.description) ||
        site.description

    /**
     * Canonical
     *
     * Taken from an explicit canonicalPath (static pages) or the node slug
     * (articles and collections). Deliberately NOT from `location`: during
     * `gatsby build` it resolves to "/" for every page. With neither source we
     * emit no canonical at all, which is safer than pointing every page home.
     */
    const canonicalSource = canonicalPath || slug || null
    const canonicalUrl =
        baseUrl && canonicalSource ? absoluteUrl(baseUrl, canonicalSource) : null

    /**
     * hreflang alternates
     *
     * The site exists as two separate builds (de on www, en on en.) with
     * different slugs, so the pairing lives in seo-hreflang.js rather than in a
     * routing convention. Without this, Google treats the two language versions
     * as unrelated pages competing for the same intent.
     *
     * Rules that matter here:
     *  - the set must be reciprocal; seo-hreflang.js refuses to generate a
     *    one-way map
     *  - every alternate must point at a 200, so a page with no counterpart gets
     *    no alternates at all rather than a link to nothing
     *  - x-default points at the German page, which is the primary market
     */
    const alternates = alternatesFor(canonicalSource)
    const hreflangLinks = alternates
        ? [
              <link
                  key='hl-de'
                  rel='alternate'
                  hreflang='de'
                  href={alternates.de}
              />,
              <link
                  key='hl-en'
                  rel='alternate'
                  hreflang='en'
                  href={alternates.en}
              />,
              <link
                  key='hl-default'
                  rel='alternate'
                  hreflang='x-default'
                  href={alternates.de}
              />
          ]
        : []

    /**
     * Images
     */
    const thumbnailPath = thumbnail && thumbnail.hero && thumbnail.hero.src
    const thumbnailUrl = thumbnailPath ? absoluteUrl(baseUrl, thumbnailPath) : null
    const defaultThumbnail = staticFileUrls.get(DEFAULT_OG_THUMBNAIL)
    const imageUrl =
        thumbnailUrl ||
        (defaultThumbnail ? absoluteUrl(baseUrl, defaultThumbnail) : null)

    const isoPublished = toIsoDate(dateISO) || toIsoDate(date)
    const isoModified = toIsoDate(dateModified) || isoPublished

    /**
     * Meta Tags
     */

    const metaTags = []
    const addMeta = (key, value, content) => {
        if (content === undefined || content === null || content === '') return
        metaTags.push({ [key]: value, content })
    }

    addMeta('itemprop', 'name', pageTitle)
    addMeta('itemprop', 'description', pageDescription)
    addMeta('name', 'description', pageDescription)

    addMeta('property', 'og:title', pageTitle)
    addMeta('property', 'og:description', pageDescription)
    addMeta('property', 'og:type', type || (isoPublished ? 'article' : 'website'))
    addMeta('property', 'og:site_name', site.name)
    addMeta('property', 'og:url', canonicalUrl)
    addMeta('property', 'og:image', imageUrl)
    addMeta('property', 'og:locale', language === 'de' ? 'de_DE' : 'en_GB')

    addMeta('name', 'twitter:card', imageUrl ? 'summary_large_image' : 'summary')
    addMeta('name', 'twitter:site', site.name)
    addMeta('name', 'twitter:title', pageTitle)
    addMeta('name', 'twitter:description', pageDescription)
    addMeta('name', 'twitter:image', imageUrl)
    addMeta('name', 'twitter:creator', twitter.url)

    if (keywords && keywords.length > 0) {
        addMeta('name', 'keywords', keywords.join(', '))
    }

    if (isoPublished) {
        addMeta('property', 'article:published_time', isoPublished)
    }

    if (isoModified && isoPublished && isoModified !== isoPublished) {
        addMeta('property', 'article:modified_time', isoModified)
    }

    if (timeToRead) {
        addMeta('name', 'twitter:label1', 'Lesezeit')
        addMeta('name', 'twitter:data1', `${timeToRead} Min.`)
    }

    if (noindex) {
        addMeta('name', 'robots', 'noindex, follow')
    }

    if (meta) {
        metaTags.push(meta)
    }

    /**
     * Structured Data (JSON-LD)
     */

    const scripts = []
    const organizationId = baseUrl ? `${baseUrl}/#organization` : null
    const websiteId = baseUrl ? `${baseUrl}/#website` : null

    if (baseUrl) {
        scripts.push(
            helmetJsonLdProp({
                '@context': 'https://schema.org',
                '@type': 'Organization',
                '@id': organizationId,
                name: brandName,
                url: `${baseUrl}/`,
                logo: {
                    '@type': 'ImageObject',
                    url: absoluteUrl(baseUrl, PUBLISHER_LOGO_PATH),
                    width: 512,
                    height: 512
                },
                email: site.email,
                telephone: site.phone,
                address: site.address
                    ? {
                          '@type': 'PostalAddress',
                          addressLocality: site.address,
                          addressCountry: 'DE'
                      }
                    : undefined,
                sameAs: (site.social || [])
                    .map(entry => entry && entry.url)
                    .filter(Boolean)
            })
        )
        scripts.push(
            helmetJsonLdProp({
                '@context': 'https://schema.org',
                '@type': 'WebSite',
                '@id': websiteId,
                url: `${baseUrl}/`,
                name: brandName,
                inLanguage: language,
                publisher: { '@id': organizationId }
            })
        )
    }

    // Article
    if (pageTitle && isoPublished) {
        const authorNode =
            author && author.name
                ? {
                      '@type': 'Organization',
                      name: author.name,
                      url: author.slug
                          ? absoluteUrl(baseUrl, author.slug)
                          : undefined
                  }
                : undefined

        scripts.push(
            helmetJsonLdProp({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: stripBrandSuffix(pageTitle, brandName),
                description: pageDescription || undefined,
                image: imageUrl ? [imageUrl] : undefined,
                datePublished: isoPublished,
                dateModified: isoModified || undefined,
                inLanguage: language,
                author: authorNode,
                publisher: { '@id': organizationId },
                mainEntityOfPage: canonicalUrl
                    ? { '@type': 'WebPage', '@id': canonicalUrl }
                    : undefined,
                url: canonicalUrl || undefined
            })
        )
    }

    // Breadcrumb: Home -> category -> current
    const breadcrumbItems = []
    if (baseUrl) {
        breadcrumbItems.push({ name: brandName, url: `${baseUrl}/` })
        if (category && category.name) {
            breadcrumbItems.push({
                name: category.name,
                url: absoluteUrl(baseUrl, category.slug)
            })
        }
        if (canonicalUrl && canonicalSource && canonicalSource !== '/') {
            breadcrumbItems.push({
                name: stripBrandSuffix(pageTitle, brandName),
                url: canonicalUrl
            })
        }
    }

    if (breadcrumbItems.length > 1) {
        scripts.push(
            helmetJsonLdProp({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: breadcrumbItems.map((item, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    name: item.name,
                    item: item.url
                }))
            })
        )
    }

    const canonicalLink = canonicalUrl ? (
        <link rel="canonical" href={canonicalUrl} />
    ) : null

    return (
        <Helmet
            htmlAttributes={{
                lang: language
            }}
            title={helmetTitle}
            meta={metaTags}
            script={scripts}
        >
            {/*
              Emitted as a child, not via the `link` prop: react-helmet drops
              the `link` prop entirely when any child <link> is present, which
              silently removed the canonical on every page.
            */}
            {canonicalLink}
            {hreflangLinks}
            <link
                rel="icon"
                // We get a url for this via graphql because we don't want to
                // inline it, which webpack would do for us if we imported it
                // directly
                // Querying publicURL on a file node causes it to be copied
                href={staticFileUrls.get(FAVICON)}
                type="image/svg+xml"
                sizes="any"
            />
            {children}
        </Helmet>
    )
}


export default Seo
